import { Link } from "react-router-dom";
import { HeartHandshake, MessageCircle, BookOpen, Calendar, ArrowRight, Download, Users } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { listResources, resourceDownloadUrl, getEvents } from "../lib/api";

export default function Parents() {
  const { data: res = [] } = useQuery({
    queryKey: ["public-resources", "Parent Guides"],
    queryFn: () => listResources("Parent Guides"),
  });
  const { data: events = [] } = useQuery({ queryKey: ["events"], queryFn: () => getEvents(true) });

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A0A0A] text-white" data-testid="parents-hero">
        <div className="noise" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#6B21A8]/35 via-transparent to-transparent" />
        <div className="container-x relative py-24 md:py-32">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-line" />
              <span className="overline text-[#F5B90A]">Family Empowerment Center</span>
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.02] tracking-tight">
              Raising young men is a <span className="italic text-[#F5B90A]">team effort.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-white/80">
              You know your son best. We're here to walk alongside you — with mentorship, prevention tools,
              wellness guidance, and a community that believes in your family.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/enroll" className="btn-gold" data-testid="parents-enroll"><ArrowRight className="h-4 w-4" /> Enroll Your Child</Link>
              <Link to="/request-help" className="btn-outline-white" data-testid="parents-help">Request Support</Link>
            </div>
          </div>
        </div>
      </section>

      {/* QUICK PATHS */}
      <section className="bg-white py-20" data-testid="parents-paths">
        <div className="container-x">
          <div className="grid gap-5 md:grid-cols-4">
            {[
              { icon: HeartHandshake, title: "Program Information", body: "Learn about every program your son can join.", to: "/programs" },
              { icon: BookOpen, title: "Orientation Materials", body: "What to expect as a Change Center family.", to: "/resources?category=Parent+Guides" },
              { icon: MessageCircle, title: "Conversation Guides", body: "Starting hard conversations about drugs, feelings, and friendships.", to: "/strong-minds-center" },
              { icon: Calendar, title: "Family Workshops", body: "Join our Family Prevention Academy nights.", to: "/events" },
            ].map((p, i) => (
              <Link key={p.title} to={p.to} className="card-editorial p-6 block" data-testid={`parent-path-${i}`}>
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#6B21A8]/10 text-[#6B21A8]">
                  <p.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-display text-lg font-bold">{p.title}</h3>
                <p className="mt-2 text-sm text-black/70">{p.body}</p>
                <p className="mt-4 text-sm font-semibold text-[#6B21A8]">Open →</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAMILY PREVENTION ACADEMY */}
      <section className="bg-[#F7F3EC] py-20 md:py-28" data-testid="parents-academy">
        <div className="container-x grid gap-10 md:grid-cols-12 items-start">
          <div className="md:col-span-5">
            <span className="overline text-[#6B21A8]">Family Prevention Academy</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-2">Learn together. Lead together.</h2>
            <p className="mt-5 text-black/70 text-lg">
              Monthly caregiver workshops and practical family learning activities designed to extend the Strong Minds
              curriculum into your home. Each session includes a take-home guide and family activity.
            </p>
            <div className="mt-8">
              <Link to="/events" className="btn-primary" data-testid="parents-see-workshops">See Upcoming Workshops</Link>
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="grid gap-4">
              {[
                "Talking to your son about drugs — before someone else does",
                "What to say when they're angry, anxious, or shutting down",
                "Setting phone, friend, and screen boundaries that stick",
                "Building a household culture of honesty and respect",
                "Recognizing when extra professional support is needed",
              ].map((t, i) => (
                <div key={i} className="rounded-lg bg-white p-5 border border-black/5" data-testid={`parent-topic-${i}`}>
                  <p className="font-semibold">{t}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* UPCOMING EVENTS */}
      {events.length > 0 && (
        <section className="bg-white py-20" data-testid="parents-events">
          <div className="container-x">
            <div className="flex items-end justify-between flex-wrap gap-3">
              <h2 className="font-display text-3xl md:text-4xl font-bold">Upcoming Family Events</h2>
              <Link to="/events" className="text-sm font-semibold text-[#6B21A8] hover:underline">All events →</Link>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {events.slice(0, 3).map((e) => (
                <Link key={e.id} to="/events" className="card-editorial p-6 block">
                  <p className="overline text-[#6B21A8]">{e.category || "Event"}</p>
                  <h4 className="mt-2 font-display text-lg font-bold">{e.title}</h4>
                  <p className="mt-2 text-sm text-black/60">{e.date}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PARENT RESOURCES */}
      <section className="bg-[#0A0A0A] text-white py-20" data-testid="parents-resources">
        <div className="container-x">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <span className="overline text-[#F5B90A]">Downloadable Guides</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold mt-2 text-white">Parent Toolkit</h2>
            </div>
            <Link to="/resources" className="text-sm font-semibold text-[#F5B90A] hover:underline">All resources →</Link>
          </div>
          {res.length === 0 ? (
            <p className="mt-10 text-white/60 italic">Parent guides are being prepared and will appear here once published.</p>
          ) : (
            <div className="mt-10 grid gap-4 md:grid-cols-3">
              {res.map((r) => (
                <a key={r.id} href={resourceDownloadUrl(r.id)} target="_blank" rel="noreferrer" className="rounded-lg border border-white/15 p-6 hover:border-[#F5B90A] transition-colors" data-testid={`parent-resource-${r.id}`}>
                  <p className="overline text-[#F5B90A]">{r.category}</p>
                  <h4 className="mt-2 font-display text-lg font-bold">{r.title}</h4>
                  {r.description && <p className="mt-2 text-sm text-white/70">{r.description}</p>}
                  <p className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#F5B90A]">
                    <Download className="h-4 w-4" /> Download
                  </p>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* HELP CTA */}
      <section className="bg-white py-20" data-testid="parents-help-cta">
        <div className="container-x text-center max-w-3xl mx-auto">
          <Users className="h-10 w-10 mx-auto text-[#6B21A8]" />
          <h2 className="mt-6 font-display text-4xl md:text-5xl font-bold">We're here when you need us.</h2>
          <p className="mt-4 text-black/70 text-lg">
            Confidential family support, program questions, or help finding the right next step — reach out any time.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/request-help" className="btn-primary">Request Confidential Support</Link>
            <Link to="/contact" className="btn-outline">Contact Us</Link>
          </div>
        </div>
      </section>
    </>
  );
}
